/**
 * Focused-drill host. One URL shape:
 *   /drill/new?mode=...&kickoff=...&drill_target=...&drill_difficulty=...
 *
 * Mirror of `chat/routes/Chat.tsx` for the new-session path (there is no
 * `:id` resume branch — resuming/deep-diving stays on `/chat/{id}`, the
 * conversational view). Keys `DrillView` on the search string so two
 * `/drill/new` launches that differ only in query params (e.g. Today's
 * Drill "Continue → next rung") force a fresh mount + re-fire the
 * kickoff effect.
 */

import { useSearchParams } from "react-router-dom";

import { DrillView } from "@/features/drills/components/DrillView";
import {
  SESSION_MODES,
  type DrillDifficulty,
  type SessionMode,
} from "@/shared/models";

const VALID_DIFFICULTIES = new Set<DrillDifficulty>([
  "foundational",
  "default",
  "edge_case",
]);

function parseDifficulty(value: string | null): DrillDifficulty | undefined {
  if (!value) return undefined;
  return VALID_DIFFICULTIES.has(value as DrillDifficulty)
    ? (value as DrillDifficulty)
    : undefined;
}

export function Drill() {
  const [search] = useSearchParams();

  const mode = (search.get("mode") ?? "spot_drill") as SessionMode;
  const kickoff = search.get("kickoff") ?? undefined;
  const drillTarget = search.get("drill_target") ?? undefined;
  const drillDifficulty = parseDifficulty(search.get("drill_difficulty"));
  const dailyChallengeDate = search.get("daily_challenge_date") ?? undefined;
  const title = dailyChallengeDate
    ? "Today's Drill"
    : SESSION_MODES[mode]?.title ?? "Drill";

  return (
    <DrillView
      key={search.toString()}
      initialMode={mode}
      kickoffMessage={kickoff}
      drillTarget={drillTarget}
      drillDifficulty={drillDifficulty}
      dailyChallengeDate={dailyChallengeDate}
      title={title}
    />
  );
}
